import { useState } from "react";
import CountryAdminTable from "./CountryAdminTable";
import AddCountryAdminModal from "./AddCountryAdminModal";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { toast } from "react-toastify";
import { PageHeader, Pagination } from "../../../components/ui";
import DeletModel from "../../../components/model/deleteModel";
import { strings } from "../../../strings/strings";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { DELETE_COUNTRY_ADMIN, GET_COUNTRY_ADMIN } from "../../../api/url";
import { useQueryClient } from "@tanstack/react-query";

const CountryAdmin = () => {
  const queryClient = useQueryClient();
  const deleteAdminMutation = useAppMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const params = { page, limit };

  const {
    data: countryAdminData,
    refetch,
    isLoading,
  } = useAppQuery({
    queryKey: [...QUERY_KEYS.COUNTRY_ADMIN, page, limit],
    url: GET_COUNTRY_ADMIN,
    method: "GET",
    params,
  });

  const rawList =
    countryAdminData?.results?.data ||
    countryAdminData?.data?.results ||
    countryAdminData?.results ||
    countryAdminData?.data ||
    [];

  const countryList = rawList.map((item) => ({
    id: item.id || item._id,
    countryName: item.country || item.countryName,
    country_code: item.country_code || item.countryCode || "",
    username: item.username,
    email: item.email,
    password: item.password || item.pwd || item.pass || "",
  }));

  const totalPages =
    countryAdminData?.results?.totalPages ||
    countryAdminData?.results?.pagination?.totalPages ||
    countryAdminData?.totalPages ||
    countryAdminData?.pagination?.totalPages ||
    Math.ceil((countryAdminData?.results?.totalItems || countryAdminData?.totalItems || 0) / limit) ||
    1;

  const totalItems =
    countryAdminData?.results?.totalItems ||
    countryAdminData?.results?.pagination?.totalItems ||
    countryAdminData?.totalItems ||
    countryAdminData?.pagination?.totalItems ||
    0;

  const handleAddSuccess = () => {
    refetch();
    setModalOpen(false);
    setSelectedAdmin(null);
  };

  const handleEdit = (admin) => {
    setSelectedAdmin(admin);
    setModalOpen(true);
  };

  const handleDelete = (admin) => {
    setAdminToDelete(admin);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!adminToDelete) return;

    deleteAdminMutation.mutate({
      url: `${DELETE_COUNTRY_ADMIN}?id=${adminToDelete.id}`,
      method: "DELETE",
    }, {
      onSuccess: () => {
        toast.success("Admin deleted successfully");
        queryClient.invalidateQueries(QUERY_KEYS.COUNTRY_ADMIN);
        setIsDeleteModalOpen(false);
        setAdminToDelete(null);
      },
      onError: (err) => {
        toast.error(err.message);
      }
    });
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAdminToDelete(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAdmin(null);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pagination = {
    currentPage: page,
    totalPages,
    totalItems,
    itemsPerPage: limit,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1,
  };

  return (
    <div>
      <PageHeader title={strings.countryadmintitle} description={strings.countryadmindescription}>
        <button
          onClick={() => setModalOpen(true)}
          className="px-5 py-2.5 bg-primary text-white rounded-lg font-medium hover:opacity-90 shadow-sm transition-all"
        >
        {strings.addcountryadminbutton}
        </button>
      </PageHeader>

      <CountryAdminTable
        countryList={countryList}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {!isLoading && (
        <div className="mt-6">
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            currentItemsCount={countryList.length}
            showInfo={true}
            showPageNumbers={true}
          />
        </div>
      )}

      <AddCountryAdminModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onSuccess={handleAddSuccess}
        initialData={selectedAdmin}
        isEdit={!!selectedAdmin}
      />

      {isDeleteModalOpen && (
        <DeletModel
          handleConfirmDelete={confirmDelete}
          handleCancelDelete={handleCloseDeleteModal}
          isLoading={deleteAdminMutation.isPending}
        />
      )}
    </div>
  );
};

export default CountryAdmin;
